/**
 * 推薦・紹介文ダイアログのセッション状態。
 * FormatEdit / YagiofferEdit から open、App 配下の IntroductionDialog が購読。
 */
import axios from 'axios'
import dateFormat from 'dateformat'
import localforage from 'localforage'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAuth } from '@/composables/useAuth'
import introductionRequestSamples from '@/assets/format-edit/introduction-request-samples.json'

export type IntroductionOpenPayload = {
  formatId: string
  previewHtml: string
}

type BasicData = {
  lastName?: string
  firstName?: string
  email?: string
}

export type IntroductionSubmitHost = {
  isOnline: boolean
  isMobileDevice: boolean
  requestIntroductionUrl: string
  message: (opts: Record<string, unknown>) => void
}

const RELATIONSHIP_OPTIONS = [
  { value: '両親・親族', label: '両親・親族' },
  { value: '担当教授', label: '担当教授' },
  { value: '知人', label: '知人' },
  { value: '上司（アルバイト先など）', label: '上司（アルバイト先など）' },
  { value: '同僚', label: '同僚' },
  { value: 'その他', label: 'その他' }
] as const

export const useIntroductionDialogStore = defineStore(
  'introductionDialog',
  () => {
    const visible = ref(false)
    const sampleVisible = ref(false)
    const formatId = ref('')
    const previewHtml = ref('')
    const isSended = ref(false)
    const fullscreenLoading = ref(false)
    const username = ref('')
    const email = ref('')
    const introEmail = ref('')
    const introName = ref('')
    const relationship = ref('')
    const requestStatement = ref('')
    const relationshipEtc = ref('')
    const limitDate = ref<string | Date>('')

    const requestSamples = introductionRequestSamples.request_statement
    const isRelationshipEtc = computed(() => relationship.value === 'その他')

    function resetFormFields() {
      isSended.value = false
      fullscreenLoading.value = false
      introEmail.value = ''
      introName.value = ''
      relationship.value = ''
      requestStatement.value = ''
      relationshipEtc.value = ''
      limitDate.value = ''
      sampleVisible.value = false
    }

    async function open(payload: IntroductionOpenPayload) {
      formatId.value = payload.formatId
      previewHtml.value = payload.previewHtml
      resetFormFields()
      try {
        const basicData = await localforage.getItem<BasicData>('basicData')
        if (basicData) {
          username.value =
            `${basicData.lastName || ''} ${basicData.firstName || ''}`.trim()
          email.value = basicData.email || ''
        } else {
          username.value = ''
          email.value = ''
        }
      } catch (err) {
        console.error(err)
        username.value = ''
        email.value = ''
      }
      visible.value = true
    }

    function close() {
      visible.value = false
      sampleVisible.value = false
    }

    function setVisible(value: boolean) {
      if (value) {
        visible.value = true
        return
      }
      if (visible.value) close()
    }

    function setSampleVisible(value: boolean) {
      sampleVisible.value = value
    }

    function applySample(description: string) {
      requestStatement.value += description.replace(/<br>/gi, '\n') + '\n'
      sampleVisible.value = false
    }

    function createFilename(ext: string) {
      const now = new Date()
      const prefix = process.env.VUE_APP_DOWNLOAD_FILENAME_PREFIX || ''
      const dateString = dateFormat(now, '_yyyy-mm-dd_HH-MM-ss')
      return `${prefix}${formatId.value}${dateString}.${ext}`
    }

    async function submit(host: IntroductionSubmitHost) {
      const { user, getClient } = useAuth()

      if (!host.isOnline) {
        host.message({
          message:
            '現在オフラインです。インターネットにつながってから再度お試しください',
          type: 'warning'
        })
        return
      }
      if (email.value === '') {
        host.message({
          message: 'メールアドレスを入力してください',
          type: 'error'
        })
        return
      }
      console.error(dateFormat(limitDate.value, 'yyyy/mm/dd'))

      const client = getClient()
      await client?.reloadToken?.()
      const accessToken = client?.accessToken
      const authUser = user.value
      if (!accessToken || !authUser?.email) {
        host.message({
          message: 'ログインが必要です',
          type: 'warning'
        })
        return
      }
      if (!introName.value) {
        host.message({
          message: 'お願いする相手の姓名を入力してください',
          type: 'warning'
        })
        return
      }
      if (!introEmail.value) {
        host.message({
          message: 'お願いする相手のメールアドレスを入力してください',
          type: 'warning'
        })
        return
      }
      if (!requestStatement.value) {
        host.message({
          message: 'お願い内容を入力してください',
          type: 'warning'
        })
        return
      }

      fullscreenLoading.value = true
      const formData = new FormData()
      formData.append('app_html', previewHtml.value)
      formData.append('pdf_option', 'A4')
      formData.append('uuid', authUser.uid)
      formData.append('intro_name', introName.value)
      formData.append('request_name', username.value)
      formData.append('request_email', email.value)
      formData.append('format_id', formatId.value)
      formData.append('intro_email', introEmail.value)
      formData.append('relationship', relationship.value)
      formData.append('limit_date', dateFormat(limitDate.value, 'yyyy/mm/dd'))
      formData.append('request_statement', requestStatement.value)
      formData.append('filename', createFilename('pdf'))
      formData.append('relationship_etc', relationshipEtc.value)
      formData.append('device_flg', host.isMobileDevice ? 'SP' : 'PC')
      formData.append('version', '2')
      formData.append('reqflg', '2')

      try {
        await axios.post(host.requestIntroductionUrl, formData, {
          timeout: 60000
        })
        close()
        host.message({
          duration: 0,
          showClose: true,
          dangerouslyUseHTMLString: true,
          message: 'お願いを送信しました',
          type: 'success'
        })
      } catch (error: unknown) {
        console.error(error)
        host.message({
          duration: 0,
          showClose: true,
          message: '応募に失敗しました。入力内容を確認して再度ご応募ください',
          type: 'error'
        })
      } finally {
        fullscreenLoading.value = false
      }
    }

    return {
      visible,
      sampleVisible,
      formatId,
      previewHtml,
      isSended,
      fullscreenLoading,
      username,
      email,
      introEmail,
      introName,
      relationship,
      requestStatement,
      relationshipEtc,
      limitDate,
      requestSamples,
      relationshipOptions: RELATIONSHIP_OPTIONS,
      isRelationshipEtc,
      open,
      close,
      setVisible,
      setSampleVisible,
      applySample,
      submit
    }
  }
)
